
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { cache } from '@common/util/cache.operator';
import { ChecklistTemplateCreateDialogComponent, ChecklistTemplateCreateDialogData } from '@portal-core/checklist-templates/components/checklist-template-create-dialog/checklist-template-create-dialog.component';
import { CentralPermissions } from '@portal-core/permissions/enums/central-permissions.enum';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { ProjectChecklistProfile } from '@portal-core/profiles/models/project-checklist-profile.model';
import { ProfileBase } from '@portal-core/profiles/util/profile.base';
import { ProjectChecklistColumnsFormComponent } from '@portal-core/project-checklists/components/project-checklist-columns-form/project-checklist-columns-form.component';
import { ProjectChecklistRowsFormComponent } from '@portal-core/project-checklists/components/project-checklist-rows-form/project-checklist-rows-form.component';
import { ProjectChecklistSettingsFormComponent } from '@portal-core/project-checklists/components/project-checklist-settings-form/project-checklist-settings-form.component';
import { ProjectChecklistProfileTab } from '@portal-core/project-checklists/enums/project-checklist-profile-tab.enum';
import { ProjectChecklistType } from '@portal-core/project-checklists/enums/project-checklist-type.enum';
import { ProjectChecklistAnalytics } from '@portal-core/project-checklists/models/project-checklist-analytics.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { MC_FOCUSABLE } from '@portal-core/ui/focus/util/focusable.token';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'mc-project-checklist-profile',
  templateUrl: './project-checklist-profile.component.html',
  styleUrls: ['./project-checklist-profile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MC_FOCUSABLE, useExisting: ProjectChecklistProfileComponent }
  ]
})
export class ProjectChecklistProfileComponent extends ProfileBase<ProjectChecklistProfileTab> implements OnInit {
  @Input() checklistProfile: ProjectChecklistProfile;
  @Input() showOverview: boolean = true;
  @Input() showSettings: boolean = true;
  @Input() showColumns: boolean = true;
  @Input() showRows: boolean = true;
  @Input() showActivity: boolean = true;
  @Input() showSaveTemplate: boolean = true;
  @Input() showDelete: boolean = true;

  @Output() cancel: EventEmitter<void> = new EventEmitter<void>();
  @Output() navigate: EventEmitter<void> = new EventEmitter<void>();
  @Output() saved: EventEmitter<ProjectChecklistProfileTab> = new EventEmitter<ProjectChecklistProfileTab>();

  @InputObservable('checklistProfile') checklistProfile$: Observable<ProjectChecklistProfile>;
  @InputObservable('showOverview') showOverview$: Observable<boolean>;
  @InputObservable('showSettings') showSettings$: Observable<boolean>;
  @InputObservable('showColumns') showColumns$: Observable<boolean>;
  @InputObservable('showRows') showRows$: Observable<boolean>;
  @InputObservable('showActivity') showActivity$: Observable<boolean>;
  @InputObservable('showSaveTemplate') showSaveTemplate$: Observable<boolean>;
  @InputObservable('showDelete') showDelete$: Observable<boolean>;

  @ViewChild('checklistSettingsForm', { static: false }) checklistSettingsForm: ProjectChecklistSettingsFormComponent;
  @ViewChild('checklistColumnsForm', { static: false }) checklistColumnsForm: ProjectChecklistColumnsFormComponent;
  @ViewChild('checklistRowsForm', { static: false }) checklistRowsForm: ProjectChecklistRowsFormComponent;

  ProjectChecklistProfileTab: typeof ProjectChecklistProfileTab = ProjectChecklistProfileTab;
  ProjectChecklistType: typeof ProjectChecklistType = ProjectChecklistType;

  addAnalyticsToChecklist$: Observable<ProjectChecklistAnalytics>;
  checklist$: Observable<ProjectChecklist>;
  project$: Observable<Project>;
  showOverviewTab$: Observable<boolean>;
  showSettingsTab$: Observable<boolean>;
  showColumnsTab$: Observable<boolean>;
  showRowsTab$: Observable<boolean>;
  showActivityTab$: Observable<boolean>;
  showSaveAsTemplate$: Observable<boolean>;
  showDeleteTab$: Observable<boolean>;
  userCanManageChecklists$: Observable<boolean>;


  public get requirePromptOnClose(): boolean {
    return this.dirty;
  }

  get valid(): boolean {
    return this.checklistSettingsForm?.valid;
  }

  get dirty(): boolean {
    switch (this.profileTab) {
      case ProjectChecklistProfileTab.Settings:
        return this.checklistSettingsForm?.dirty;
      case ProjectChecklistProfileTab.Columns:
        return this.checklistColumnsForm?.dirty;
      case ProjectChecklistProfileTab.Rows:
        return this.checklistRowsForm?.dirty;
      default:
        return false;
    }
  }

  constructor(
    protected dialog: MatDialog,
    protected cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
    private projectsService: ProjectsService
  ) {
    super(dialog, cdr);
  }

  ngOnInit() {
    super.ngOnInit();

    // Use the checklist from the profile data so that it doesn't update while the user is viewing/editing it
    this.checklist$ = this.checklistProfile$.pipe(
      map(checklistProfile => checklistProfile ? checklistProfile.Checklist : null),
      cache()
    );

    this.project$ = this.checklistProfile$.pipe(
      // The project is returned in the profile data so there is no need to make a request to the API
      switchMap(checklistProfile => typeof checklistProfile?.Project?.Id === 'number' ? this.projectsService.getItemById$(checklistProfile.Project.Id, { allowApiRequest: false }) : of(null)),
      cache()
    );

    this.userCanManageChecklists$ = this.permissionsService.currentUserHasPermission$(CentralPermissions.ManageChecklists);

    this.showOverviewTab$ = this.showOverview$;
    this.showSettingsTab$ = combineLatest([this.showSettings$, this.userCanManageChecklists$]).pipe(
      map(([showSettings, userCanManageChecklists]) => showSettings && userCanManageChecklists)
    );
    this.showColumnsTab$ = combineLatest([this.showColumns$, this.userCanManageChecklists$]).pipe(
      map(([showColumns, userCanManageChecklists]) => showColumns && userCanManageChecklists)
    );
    this.showRowsTab$ = combineLatest([this.showRows$, this.userCanManageChecklists$]).pipe(
      map(([showRows, userCanManageChecklists]) => showRows && userCanManageChecklists)
    );
    this.showActivityTab$ = this.showActivity$;
    this.showSaveAsTemplate$ = this.userCanManageChecklists$;
    this.showDeleteTab$ = combineLatest([this.showDelete$, this.userCanManageChecklists$]).pipe(
      map(([showDelete, userCanManageChecklists]) => showDelete && userCanManageChecklists)
    );
  }

  onCancelClicked() {
    this.cancel.emit();
  }

  onChecklistDeleted() {
    this.saved.emit(ProjectChecklistProfileTab.Delete);
  }

  onSaveAsTemplate() {
    this.dialog.open<ChecklistTemplateCreateDialogComponent, ChecklistTemplateCreateDialogData>(ChecklistTemplateCreateDialogComponent, {
      ...ChecklistTemplateCreateDialogComponent.DialogConfig,
      data: {
        licenseId: this.checklistProfile?.Checklist?.LicenseId,
        checklist: this.checklistProfile?.Checklist ?? null
      }
    });
  }

  onSaveClicked(tab: ProjectChecklistProfileTab) {
    this.saved.emit(tab);
  }
}
